import React, { createContext, useContext, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import * as Features from '../../../../../shared/Features';
import { Translate } from '../../../../../shared/LanguageService';
import history from '../../../../../shared/history';
import TooltipText from '../../../../../shared/feedback/TooltipText';
import { TranslatedLabels } from '../../../../../shared/types';
import { useUserContext } from '../../../../../shared/context/user-context';
import { Spacer } from '../Spacer';
import { useFeatureFlag } from '../../../../../shared/featureFlags/useFeatureFlag';
import { FeatureFlagKeys } from '../../../../../shared/featureFlags/featureFlagKeys';
import SE360Logo from './SE360Logo';
import SystemVersion from './SystemVersion';
import AdvancedNavbarSearch from './search/AdvancedNavbarSearch';
import NewsBellDelegate from './newsComponent/NewsBellDelegate';
import { canUseNavbarSearch } from './search/canUseNavbarSearch';
import ProfileContext from './userProfile/ProfileContext';
import { SnowInHeader } from './seasonal/SnowInHeader';
import EditTranslationsIconButton from '../../../../../features/translationEditor/view/EditTranslationsIconButton';
import { canEditTranslations } from '../../../../../features/translationEditor/state/canEditTranslations';

export type Country = {
    id: number;
    name: string;
};

type HeadBarProps = {
    labels: TranslatedLabels;
    site: string;
};

type ExternalProps = {
    systemVersion: string;
    site: string;
};

let ImpersonatedCountryContext = createContext();

export function useImpersonatedCountryContext() {
    const context = useContext(ImpersonatedCountryContext);
    if (!context) {
        throw new Error(
            'Impersonated Country Context can not be accessed outside ImpersonatedCountryContext.Provider'
        );
    }

    return context;
}

function HeadBar({ labels, site }: HeadBarProps) {
    let [impersonatedCountry, setImpersonatedCountry] = useState<Country | null>(null);
    let { user } = useUserContext();

    let isAuthenticated = user.isAuthenticated;

    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    let shouldShowSystemVersion =
        !isSmallerScreen &&
        isAuthenticated &&
        Features.hasFeature(Features.ViewSiteVersionNumber, user);
    let isBrokerSuperSearchEnabled = useFeatureFlag(FeatureFlagKeys.BrokerSuperSearch, false);
    let shouldShowAdvancedNavbarSearch = canUseNavbarSearch(user, isBrokerSuperSearchEnabled);
    let shouldShowFavoriteImagesLink =
        isAuthenticated && Features.hasFeature(Features.FavoriteImages, user);

    let shouldShowSnow = useFeatureFlag(FeatureFlagKeys.ShowSnowLogo, false);

    function handleClickFavoriteImagesLink() {
        history.push('/favorite-images');
    }

    return (
        <AppBar position="fixed">
            {shouldShowSnow ? <SnowInHeader /> : null}
            <Toolbar
                style={{ minHeight: '48px' }}
                sx={{
                    [theme.breakpoints.up('sm')]: {
                        paddingLeft: '16px',
                        paddingRight: '8px'
                    },
                    [theme.breakpoints.down('sm')]: {
                        paddingLeft: '16px',
                        paddingRight: '8px'
                    }
                }}
            >
                {shouldShowSystemVersion ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SE360Logo site={site} />
                        <Spacer />
                        <Spacer />
                        <SystemVersion systemVersion={labels.systemVersion} />
                    </Box>
                ) : (
                    <SE360Logo site={site} />
                )}

                {!isSmallerScreen ? <Box sx={{ flexGrow: 1 }} /> : <Spacer />}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: isSmallerScreen ? '1 0 auto' : undefined
                    }}
                >
                    {canEditTranslations(user) ? <EditTranslationsIconButton /> : null}
                    {user && (
                        <Box sx={{ paddingRight: '15px' }}>
                            <NewsBellDelegate />
                        </Box>
                    )}
                    {isAuthenticated ? (
                        <ImpersonatedCountryContext.Provider
                            value={{ impersonatedCountry, setImpersonatedCountry }}
                        >
                            <ProfileContext />
                        </ImpersonatedCountryContext.Provider>
                    ) : null}
                    {shouldShowAdvancedNavbarSearch ? <AdvancedNavbarSearch /> : null}
                    {shouldShowFavoriteImagesLink ? (
                        <Tooltip
                            title={
                                <TooltipText variant="body1">
                                    {labels.favoriteImagesTooltip}
                                </TooltipText>
                            }
                            placement="bottom"
                        >
                            <IconButton
                                onClick={handleClickFavoriteImagesLink}
                                size="medium"
                                sx={{ color: '#fff' }}
                            >
                                <FavoriteBorderIcon sx={{ width: '24px', height: '24px' }} />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Box>
            </Toolbar>
        </AppBar>
    );
}

function TranslatedHeadBar({ systemVersion, site }: ExternalProps) {
    let labels: TranslatedLabels = {
        systemVersion: Translate('header/label/version', {
            version: systemVersion
        }),
        favoriteImagesTooltip: Translate('header/nav/favorite-images'),
        helpTooltip: Translate('header/nav/help')
    };
    return <HeadBar labels={labels} site={site} />;
}

export default TranslatedHeadBar;
